import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../supabaseClient";
import { errorLogService } from "../services/errorLogService";
import { TimeoutError, withTimeout } from "../utils/withTimeout";
import { createDiagnosticId } from "../utils/diagnosticsContext";
import { isAbortLikeError } from "../utils/errorClassifier";
import logger from "../utils/logger";
import { getCurrentUserProfile } from "../services/auth/currentUserProfileService";

const JWTAuthContext = createContext();
// Adaptive: 15s default (was 5s), 30s on slow/2g standalone cold start.
// 5s was killing users on 3g/slow-2g: getSession alone can take that long (see app_errors auth_init_timeout).
const AUTH_INIT_TIMEOUT_MS = 15000;
const AUTH_INIT_TIMEOUT_SLOW_MS = 30000;

const detectSlowNetwork = () => {
  try {
    if (typeof navigator !== 'undefined' && navigator.connection) {
      const et = (navigator.connection.effectiveType || '').toLowerCase();
      if (et === 'slow-2g' || et === '2g') return true;
      if (et === '3g' && navigator.connection.rtt >= 1500) return true;
    }
  } catch (e) {
    // navigator.connection недоступен — считаем сеть обычной
  }
  return false;
};
const AUTH_INIT_SLOW_MS = 3000;

const isTimeoutLikeError = (error) =>
  error instanceof TimeoutError ||
  error?.name === "TimeoutError" ||
  /timeout/i.test(error?.message || "");

export const useJWTAuth = () => {
  const context = useContext(JWTAuthContext);
  if (!context) {
    throw new Error("useJWTAuth must be used within a JWTAuthProvider");
  }
  return context;
};

export const JWTAuthProvider = ({ children: appChildren }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [error] = useState(null);
  const [effectiveRole, setEffectiveRole] = useState(null);
  const [permissions, setPermissions] = useState({
    canManageOrders: false,
    canViewReports: false,
    canManageUsers: false,
    canManageMenu: false,
  });

  const didInit = useRef(false);
  const mountTime = useRef(Date.now()); // Время монтирования — для защиты от ложных SIGNED_OUT при старте
  const loggingOut = useRef(false); // Флаг для предотвращения восстановления сессии при логауте
  const sessionRef = useRef(null);

  const isAuthenticatedRef = useRef(false); // Ref для корректной работы в замыканиях heartbeat/visibility
  const refreshFailureCount = useRef(0); // Счетчик неудачных попыток refresh
  const lastFailureTime = useRef(null); // Время последней неудачи
  const MAX_REFRESH_FAILURES = 3; // Максимум попыток перед принудительным логаутом
  const FAILURE_RESET_TIME = 60000; // Сбросить счетчик через 60 секунд
  const userDataInflight = useRef(new Map());
  const userDataCache = useRef(new Map());

  const clearAuthStorage = useCallback(() => {
    [
      "mgfood_session_info",
      "mgfood_session_backup",
      "mgfood-auth-token",
    ].forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // ignore
      }
    });
  }, []);

  const calculatePermissions = useCallback((role) => {
    const perms = {
      canManageOrders: false,
      canViewReports: false,
      canManageUsers: false,
      canManageMenu: false,
    };

    switch (role) {
      case "megaadmin":
      case "superadmin":
      case "admin":
        perms.canManageOrders = true;
        perms.canViewReports = true;
        perms.canManageUsers = true;
        perms.canManageMenu = true;
        break;
      case "assistant":
        perms.canManageOrders = true;
        break;
      case "tutor":
        perms.canManageOrders = true;
        perms.canViewReports = true;
        break;
      case "student":
        break;
      default:
        break;
    }

    return perms;
  }, []);

  const buildFallbackUserData = useCallback(
    (sessionUser, userId, userEmail, profileStatus = "error") => {
      const sessionRole =
        sessionUser?.app_metadata?.role ||
        sessionUser?.user_metadata?.role ||
        sessionUser?.role ||
        "student";
      const fallbackRole =
        profileStatus === "missing_profile" || profileStatus === "inactive_profile"
          ? "student"
          : sessionRole;

      return {
        id: sessionUser?.id || userId || null,
        auth_user_id: sessionUser?.id || userId || null,
        email: sessionUser?.email || userEmail || null,
        full_name:
          sessionUser?.user_metadata?.full_name ||
          sessionUser?.user_metadata?.name ||
          sessionUser?.email ||
          null,
        role: fallbackRole,
        class: sessionUser?.user_metadata?.class || null,
        classes: Array.isArray(sessionUser?.user_metadata?.classes)
          ? sessionUser.user_metadata.classes
          : [],
        is_active: profileStatus === "inactive_profile" ? false : true,
        updated_at: sessionUser?.updated_at || null,
      };
    },
    [],
  );

  const fetchUserData = useCallback(async (userId, userEmail) => {
    let attempts = 0;
    const maxAttempts = 3;

    const lowerEmail = userEmail?.toLowerCase();
    const cacheKey = lowerEmail || userId || "unknown";

    if (userDataCache.current.has(cacheKey)) {
      return userDataCache.current.get(cacheKey);
    }

    if (userDataInflight.current.has(cacheKey)) {
      return userDataInflight.current.get(cacheKey);
    }

    const inflightPromise = (async () => {
      while (attempts < maxAttempts) {
        try {
          const profileResult = await getCurrentUserProfile();

          if (profileResult.success && profileResult.profile) {
            return profileResult.profile;
          }

          if (
            profileResult.status === "missing_profile" ||
            profileResult.status === "inactive_profile"
          ) {
            logger.warn(
              "JWTAuthContext: current profile unavailable, using minimum compatible profile",
              {
                userId,
                userEmail,
                status: profileResult.status,
              },
            );
            return buildFallbackUserData(
              sessionRef.current?.user || null,
              userId,
              userEmail,
              profileResult.status,
            );
          }

          if (profileResult.status === "error" && attempts < maxAttempts - 1) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, 500 * attempts));
            continue;
          }

          logger.warn(
            "JWTAuthContext: profile RPC failed, using session fallback",
            {
              userId,
              userEmail,
            },
          );
          return buildFallbackUserData(
            sessionRef.current?.user || null,
            userId,
            userEmail,
          );
        } catch (err) {
          if (err instanceof TimeoutError) {
            logger.warn("User data fetch timeout", err.message);
          } else {
            logger.warn(`User data fetch attempt ${attempts + 1} failed`, err);
          }
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 500 * attempts));
          } else {
            logger.error("User data fetch error after retries", err);
            return buildFallbackUserData(
              sessionRef.current?.user || null,
              userId,
              userEmail,
            );
          }
        }
      }
      return buildFallbackUserData(
        sessionRef.current?.user || null,
        userId,
        userEmail,
      );
    })();

    userDataInflight.current.set(cacheKey, inflightPromise);

    const result = await inflightPromise;
    userDataInflight.current.delete(cacheKey);
    userDataCache.current.set(cacheKey, result);
    return result;
  }, []);

  // Получить fingerprint устройства (для привязки сессии)
  const getDeviceFingerprint = useCallback(() => {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
    ].join("|");
    // Хешируем для компактности
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(16);
  }, []);

  // Вспомогательная функция для сохранения сессии в резервное хранилище
  const backupSession = useCallback(
    (sessionData) => {
      if (sessionData) {
        try {
          const sessionWithMeta = {
            ...sessionData,
            _session_meta: {
              deviceFingerprint: getDeviceFingerprint(),
              createdAt: Date.now(),
              lastActivity: Date.now(),
            },
          };
          localStorage.setItem(
            "mgfood_session_backup",
            JSON.stringify(sessionWithMeta),
          );
        } catch (e) {
          logger.error("Failed to backup session", e);
        }
      }
    },
    [getDeviceFingerprint],
  );

  // Проверить валидность сессии (30 дней, fingerprint)
  const isSessionValid = useCallback(
    (sessionData) => {
      if (!sessionData?._session_meta) return true; // Старые сессии без мета - пропускаем

      const { deviceFingerprint, createdAt } = sessionData._session_meta;
      const currentFingerprint = getDeviceFingerprint();
      const MAX_SESSION_DAYS = 60; // Увеличено с 30 до 60 дней
      const MAX_SESSION_MS = MAX_SESSION_DAYS * 24 * 60 * 60 * 1000;

      // Проверка устройства
      if (deviceFingerprint !== currentFingerprint) {
        return false;
      }

      // Проверка срока (30 дней)
      if (Date.now() - createdAt > MAX_SESSION_MS) {
        return false;
      }

      return true;
    },
    [getDeviceFingerprint],
  );

  const loadFamilyData = useCallback(async (userEmail) => {
    try {
      if (!userEmail) return [];

      const { data: familyMembers, error: familyError } = await supabase
        .from("users_unified")
        .select("id, full_name, class, email, balance, is_active, role")
        .eq("email", userEmail)
        .eq("is_active", true)
        .order("full_name", { ascending: true });

      if (familyError) {
        logger.error("Error loading family members", familyError);
        return [];
      }

      return familyMembers || [];
    } catch (err) {
      logger.error("JWT AuthContext: Error loading family data", err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const initAuth = async () => {
      const authInitId = createDiagnosticId("auth-init");
      const authInitStartedAt = Date.now();
      let restoredFromBackup = false;
      let forcedTimeoutResolution = false;
      let justLoggedOutState = false;
      let hasResolvedSession = false;
      let slowStartupLogged = false;

      const logAuthStartupEvent = ({
        message,
        severity = "warn",
        action = "auth_init",
        error = null,
        appContext = {},
        errorMeta = {},
        runtimeContext = {},
      }) => {
        void errorLogService.logError({
          message,
          error,
          severity,
          context: {
            error_meta: {
              action,
              ...errorMeta,
            },
            runtime_context: {
              durationMs: Date.now() - authInitStartedAt,
              ...runtimeContext,
            },
            app_context: {
              module: "JWTAuthContext",
              restoredFromBackup,
              forcedTimeoutResolution,
              hasResolvedSession,
              ...appContext,
            },
            diagnostic_ids: {
              authInitId,
            },
          },
        });
      };

      // Adaptive safety timeout: longer on slow networks so users on 3g/slow-2g
      // don't get bumped to login before session resolve finishes.
      const effectiveTimeout = detectSlowNetwork()
        ? AUTH_INIT_TIMEOUT_SLOW_MS
        : AUTH_INIT_TIMEOUT_MS;
      const timeoutId = setTimeout(() => {
        logger.warn("Auth initialization timeout reached, forcing resolution");
        forcedTimeoutResolution = true;
        logAuthStartupEvent({
          message: "Инициализация авторизации превысила безопасный таймаут",
          action: "auth_init_timeout",
        });
        setAuthResolved(true);
        setLoading(false);
      }, effectiveTimeout);

      try {
        // 0. Проверяем флаг явного выхода (устанавливается при logout)
        const justLoggedOut = sessionStorage.getItem("mgfood_just_logged_out");
        if (justLoggedOut) {
          justLoggedOutState = true;
          sessionStorage.removeItem("mgfood_just_logged_out");
          setAuthResolved(true);
          setLoading(false);
          clearTimeout(timeoutId);
          return;
        }

        // 1. Пробуем получить сессию из Supabase
        let currentSession = null;
        let sessionError = null;

        try {
          const sessionResult = await withTimeout(
            supabase.auth.getSession(),
            5000,
            "supabase.auth.getSession timeout",
          );
          currentSession = sessionResult?.data?.session || null;
          sessionError = sessionResult?.error || null;
        } catch (err) {
          sessionError = err;
        }

        if (sessionError) {
          const sessionErrorLogger = isTimeoutLikeError(sessionError)
            ? logger.warn
            : logger.error;

          sessionErrorLogger("getSession error", sessionError, {
            error_meta: {
              action: "auth_get_session",
            },
            app_context: {
              module: "JWTAuthContext",
            },
            diagnostic_ids: {
              authInitId,
            },
          });
        }

        // 2. Резервное восстановление (для PWA)
        if (!currentSession && !forcedTimeoutResolution && !isTimeoutLikeError(sessionError)) {
          const backup = localStorage.getItem("mgfood_session_backup");
          if (backup) {
            let parsedBackup = null;
            try {
              parsedBackup = JSON.parse(backup);
            } catch (e) {
              logger.warn("JWTAuthContext: Backup parsing error", e, {
                error_meta: {
                  action: "auth_backup_parse",
                },
                app_context: {
                  module: "JWTAuthContext",
                },
                diagnostic_ids: {
                  authInitId,
                },
              });
              localStorage.removeItem("mgfood_session_backup");
              parsedBackup = null;
            }

            if (parsedBackup) {
              // Проверяем валидность сессии (30 дней, fingerprint)
              if (!isSessionValid(parsedBackup)) {
                localStorage.removeItem("mgfood_session_backup");
              } else if (parsedBackup.access_token) {
                try {
                  const { data: restored, error: restoreError } =
                    await withTimeout(
                      supabase.auth.setSession({
                        access_token: parsedBackup.access_token,
                        refresh_token: parsedBackup.refresh_token,
                      }),
                      5000,
                      "supabase.auth.setSession timeout",
                    );

                  if (!restoreError && restored.session) {
                    currentSession = restored.session;
                    restoredFromBackup = true;
                  } else {
                    // Skip noisy error-level log when refresh token legitimately expired —
                    // user just needs to login again. Real unexpected errors still log.
                    const isExpiredRefreshToken =
                      restoreError &&
                      typeof restoreError.message === "string" &&
                      /invalid refresh token|session expired/i.test(restoreError.message);
                    const restoreLogger = isExpiredRefreshToken
                      ? logger.debug
                      : isTimeoutLikeError(restoreError)
                        ? logger.warn
                        : logger.error;
                    restoreLogger(
                      "JWTAuthContext: Session restoration failed",
                      restoreError,
                      {
                        error_meta: {
                          action: "auth_restore_session",
                          expected_expired: !!isExpiredRefreshToken,
                        },
                        app_context: {
                          module: "JWTAuthContext",
                        },
                        diagnostic_ids: {
                          authInitId,
                        },
                      },
                    );
                    localStorage.removeItem("mgfood_session_backup");
                  }
                } catch (restoreError) {
                  const restoreLogger = isTimeoutLikeError(restoreError)
                    ? logger.warn
                    : logger.error;

                  restoreLogger(
                    "JWTAuthContext: Session restoration failed",
                    restoreError,
                    {
                      error_meta: {
                        action: "auth_restore_session",
                      },
                      app_context: {
                        module: "JWTAuthContext",
                      },
                      diagnostic_ids: {
                        authInitId,
                      },
                    },
                  );
                  localStorage.removeItem("mgfood_session_backup");
                }
              }
            }
          }
        }

        if (currentSession?.user) {
          hasResolvedSession = true;
          setUser(currentSession.user);
          setSession(currentSession);
          sessionRef.current = currentSession;
          backupSession(currentSession);
          setIsAuthenticated(true);
          isAuthenticatedRef.current = true;
          setAuthResolved(true);

          // Загружаем данные пользователя (роль, имя) — ПОСЛЕ установки isAuthenticated,
          // чтобы App.js не перенаправлял на /login пока грузятся данные
          const userData = await fetchUserData(
            currentSession.user.id,
            currentSession.user.email,
          );

          if (userData) {
            const role = userData.role || "student";
            logger.auth(
              `JWTAuthContext: Loaded role '${role}' for ${currentSession.user.email}`,
            );
            setEffectiveRole(role);
            setPermissions(calculatePermissions(role));

            setUser((prev) => ({
              ...prev,
              ...userData,
              role: role,
              full_name: userData.full_name || prev?.user_metadata?.full_name,
            }));
          }

          const durationMs = Date.now() - authInitStartedAt;
          if (durationMs >= AUTH_INIT_SLOW_MS && !forcedTimeoutResolution) {
            slowStartupLogged = true;
            logAuthStartupEvent({
              message: "Инициализация авторизации завершилась медленно",
              action: "auth_init_slow",
              appContext: {
                role: userData?.role || "student",
              },
            });
          }

          clearTimeout(timeoutId);
          setLoading(false); // Ensure loading is set to false after data fetch
          return;
        }
      } catch (err) {
        if (isAbortLikeError(err)) {
          logger.warn("Auth initialization aborted, continuing", err);
        } else {
          logger.error("Auth initialization exception", err, {
            error_meta: {
              action: "auth_init_exception",
            },
            app_context: {
              module: "JWTAuthContext",
              restoredFromBackup,
              forcedTimeoutResolution,
            },
            diagnostic_ids: {
              authInitId,
            },
          });
        }
        // Ensure auth is always resolved even on error
        setAuthResolved(true);
        setLoading(false);
      } finally {
        const durationMs = Date.now() - authInitStartedAt;
        if (
          !justLoggedOutState &&
          durationMs >= AUTH_INIT_SLOW_MS &&
          !slowStartupLogged &&
          !forcedTimeoutResolution
        ) {
          logAuthStartupEvent({
            message: "Инициализация авторизации завершилась медленно без активной сессии",
            action: "auth_init_slow_no_session",
          });
        }

        clearTimeout(timeoutId);
        setAuthResolved(true);
        setLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // Игнорируем INITIAL_SESSION если сессия не изменилась
      if (
        event === "INITIAL_SESSION" &&
        sessionRef.current?.access_token === newSession?.access_token
      ) {
        return;
      }

      // IMPORTANT: Игнорируем SIGNED_OUT во время инициализации (первые 10 секунд).
      // На мобильных PWA Supabase испускает SIGNED_OUT пока IndexedDB ещё асинхронно
      // читает токен — это ложное событие, которое не должно вызывать очистку состояния.
      const timeSinceMount = Date.now() - mountTime.current;
      if (
        event === "SIGNED_OUT" &&
        !newSession &&
        timeSinceMount < 10000 &&
        !loggingOut.current
      ) {
        return;
      }

      // Обработка события выхода
      if (event === "SIGNED_OUT" && !newSession) {
        // Инкрементируем счетчик неудач
        const now = Date.now();
        if (
          lastFailureTime.current &&
          now - lastFailureTime.current > FAILURE_RESET_TIME
        ) {
          // Сбрасываем счетчик если прошло больше минуты
          refreshFailureCount.current = 0;
        }
        refreshFailureCount.current++;
        lastFailureTime.current = now;

        // CIRCUIT BREAKER: Порог поднят с 2 до 3 (2026-09-01).
        // Одна гонка параллельных refresh (частая на PWA/multi-tab) даёт РОВНО 2 события
        // SIGNED_OUT (400 session_expired), хотя токен на самом деле живой — при пороге 2
        // это ошибочно выкидывало пользователя. Реально мёртвый токен даёт 3+ провала
        // подряд за 60с (FAILURE_RESET_TIME) и всё равно будет вычищен.
        if (refreshFailureCount.current >= 3) {
          logger.error(
            `Circuit breaker: ${refreshFailureCount.current} failures. Dead token detected, forcing full cleanup.`,
          );

          // 1. Очищаем только auth-ключи приложения
          clearAuthStorage();
          try {
            // Исправлен импорт: используем .default для доступа к методам
            import("../utils/indexedDBStorage")
              .then((m) => {
                const idb = m.default || m.indexedDBStorage;
                if (idb && typeof idb.clear === "function") {
                  idb.clear().catch(() => {});
                }
              })
              .catch(() => {});
          } catch (e) {
            // Ignore idb cleanup errors if import fails
          }

          // 2. Ставим флаг чтобы initAuth не пытался восстановить сессию
          sessionStorage.setItem("mgfood_just_logged_out", "true");
          loggingOut.current = true;

          // 3. Останавливаем внутренние таймеры Supabase
          supabase.auth.signOut({ scope: "local" }).catch(() => {});

          setEffectiveRole(null);
          setUser(null);
          setSession(null);
          sessionRef.current = null;
          setIsAuthenticated(false);
          isAuthenticatedRef.current = false;
          setAuthResolved(true);
          setPermissions({
            canManageOrders: false,
            canViewReports: false,
            canManageUsers: false,
            canManageMenu: false,
          });
          setLoading(false);
          refreshFailureCount.current = 0;
          return;
        }

        // При явном логауте - сразу очищаем
        if (loggingOut.current) {
          // Explicit logout, handled below
        } else {
          // Неявный SIGNED_OUT (от Supabase) - всё равно очищаем, т.к. токен мёртв
        }

        setEffectiveRole(null);
        setUser(null);
        setSession(null);
        sessionRef.current = null;
        setIsAuthenticated(false);
        isAuthenticatedRef.current = false;
        setAuthResolved(true);
        setPermissions({
          canManageOrders: false,
          canViewReports: false,
          canManageUsers: false,
          canManageMenu: false,
        });
        clearAuthStorage();
        try {
          // Также очищаем IndexedDB при любом SIGNED_OUT без сессии
          import("../utils/indexedDBStorage")
            .then((m) => {
              const idb = m.default || m.indexedDBStorage;
              if (idb && typeof idb.removeItem === "function") {
                idb.removeItem("mgfood-auth-token").catch(() => {});
              }
            })
            .catch(() => {});
        } catch (e) {
          // Ignore idb cleanup errors
        }

        setLoading(false);
        refreshFailureCount.current = 0; // Сброс после успешного логаута
        return;
      }

      // Удалена логика восстановления из backup при !newSession
      // т.к. backup обычно содержит тот же мёртвый токен и вызывает цикл

      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        newSession?.user
      ) {
        // Успешное обновление - сбрасываем счетчик неудач
        refreshFailureCount.current = 0;
        lastFailureTime.current = null;

        // Обновляем состояние только если данные реально изменились
        if (newSession?.access_token !== sessionRef.current?.access_token) {
          setSession(newSession);
          sessionRef.current = newSession;
          if (newSession) backupSession(newSession);
          setIsAuthenticated(true);
          isAuthenticatedRef.current = true;
        }

        setAuthResolved(true);

        // Always fetch user data on SIGNED_IN to ensure latest role/permissions
        const shouldFetchData =
          event === "SIGNED_IN" ||
          (newSession?.user && (!user || user.id !== newSession.user.id));

        if (shouldFetchData && newSession?.user) {
          setUser(newSession.user);
          fetchUserData(newSession.user.id, newSession.user.email)
            .then((userData) => {
              if (!userData) {
                setLoading(false);
                return;
              }
            setEffectiveRole(userData.role || "student");
              setPermissions(calculatePermissions(userData.role || "student"));
              setUser((prev) => ({
                ...prev,
                ...userData,
                role: userData.role || "student",
                full_name:
                  userData?.full_name || prev?.user_metadata?.full_name,
              }));
              isAuthenticatedRef.current = true;
              setLoading(false);
            })
            .catch(() => {
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      }
    });

    // (refreshSession function removed - unused local function)

    // HEARTBEAT LOGIC: Периодическая проверка и продление сессии
    const jitter = Math.floor(Math.random() * 60000);
    const heartbeatInterval = setInterval(
      async () => {
        // Используем ref (а не замкнутое isAuthenticated) для актуального значения
        if (
          isAuthenticatedRef.current &&
          !loggingOut.current &&
          refreshFailureCount.current < 3
        ) {
          try {
            const {
              data: { user: currentUser },
              error,
            } = await supabase.auth.getUser();
            if (error || !currentUser) {
              logger.warn("Heartbeat detected dead session", error);
            }
          } catch (err) {
            if (isAbortLikeError(err)) {
              logger.warn("Heartbeat aborted", err);
            } else {
              logger.error("Heartbeat exception", err, {
                error_meta: {
                  action: "heartbeat_exception",
                },
                app_context: {
                  module: "JWTAuthContext",
                },
              });
            }
          }
        }
      },
      12 * 60 * 1000 + jitter,
    );

    // Visibility change handler
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "visible" &&
        isAuthenticatedRef.current &&
        !loggingOut.current &&
        refreshFailureCount.current < 3
      ) {
        try {
          await supabase.auth.getSession();
        } catch (e) {
          logger.warn("Visibility check warning", e);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Online/Offline handler - мягкое обновление
    const handleOnline = () => {
      if (isAuthenticatedRef.current && !loggingOut.current) {
        supabase.auth
          .getSession()
          .then(({ data: { session: onlineSession } }) => {
            if (onlineSession) {
              setSession(onlineSession);
              backupSession(onlineSession);
            }
          })
          .catch(() => {});
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      subscription.unsubscribe();
      clearInterval(heartbeatInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [
    calculatePermissions,
    fetchUserData,
    backupSession,
    isSessionValid,
    clearAuthStorage,
  ]);

  const logout = useCallback(async () => {
    // 0. Устанавливаем флаг логаута, чтобы предотвратить восстановление сессии
    loggingOut.current = true;

    // Также ставим флаг в sessionStorage (переживёт редирект)
    sessionStorage.setItem("mgfood_just_logged_out", "true");

    // 1. Очищаем локальное состояние сразу
    setUser(null);
    setSession(null);
    sessionRef.current = null;
    setIsAuthenticated(false);
    isAuthenticatedRef.current = false;
    setAuthResolved(true);
    setEffectiveRole(null);
    setPermissions({
      canManageOrders: false,
      canViewReports: false,
      canManageUsers: false,
      canManageMenu: false,
    });

    // 2. Очищаем ВСЁ из localStorage (включая backup IndexedDB)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes("mgfood") ||
            key.includes("idb_backup") ||
            key.includes("supabase"))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      // Ignore localStorage clearing errors
    }

    // 3. Очищаем IndexedDB напрямую
    try {
      const m = await import("../utils/indexedDBStorage");
      const idb = m.default || m.indexedDBStorage;
      if (idb && typeof idb.clear === "function") {
        await idb.clear();
      }
    } catch (e) {
      logger.error("IndexedDB logout cleanup error", e);
    }

    // 4. Вызываем signOut в Supabase (scope: 'local' чтобы не ждать сервер)
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (err) {
      // Ignore Supabase signout errors
    }

    // 5. Небольшая задержка чтобы все cleanup завершились
    await new Promise((resolve) => setTimeout(resolve, 100));

    // 6. Делаем редирект
    window.location.href = "/login";
  }, []);

  const value = {
    user,
    session,
    isAuthenticated,
    loading,
    authResolved,
    error,
    permissions,
    logout,
    hasPermission: (p) => permissions[p] || false,
    hasRole: (r) => {
      if (!effectiveRole || !r) return false;
      return effectiveRole.toLowerCase() === r.toLowerCase();
    },
    getUserRole: () => effectiveRole,
    getJWTToken: () => session?.access_token || null,
    getFamilyData: async () => await loadFamilyData(user?.email),
  };

  return (
    <JWTAuthContext.Provider value={value}>
      {appChildren}
    </JWTAuthContext.Provider>
  );
};

export default JWTAuthContext;
